import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';

import { IconSetService } from '@coreui/icons-angular';
import { freeSet } from '@coreui/icons';

import { SessionSyncService, SessionChange } from './services/session-sync.service';

@Component({
  // tslint:disable-next-line
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [IconSetService],
})
export class AppComponent implements OnInit, OnDestroy {

  /**
   * Set when ANOTHER TAB ends or replaces the session.
   *
   * The dialog it raises is deliberately NOT dismissible. This tab is now painting a screen it has
   * no right to paint, and every request it makes from here is a 401 the user cannot explain. There
   * is exactly one thing to do about that, so there is exactly one button.
   */
  sessionLost: SessionChange | null = null;

  private sub?: Subscription;

  constructor(
    private router: Router,
    public iconSet: IconSetService,
    private sessionSync: SessionSyncService,
  ) {
    // iconSet singleton
    iconSet.icons = { ...freeSet };
  }

  ngOnInit() {
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });

    this.sessionSync.start();
    this.sub = this.sessionSync.change$.subscribe((change) => {
      // Only a tab that is actually SHOWING an authenticated screen has anything to announce.
      // localStorage belongs to the whole origin, so a tab parked on the login page watches the very
      // same keys disappear — and telling it "you have been signed out" would be nonsense.
      if (!this.isInsideApp()) return;

      this.sessionLost = change;
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private isInsideApp(): boolean {
    const url = this.router.url || '';
    return url.startsWith('/user') || url.startsWith('/vadmin') || url.startsWith('/client');
  }

  /**
   * A full page navigation — NOT a `router.navigate`.
   *
   * Navigating would leave every already-instantiated component, cached response and open socket
   * alive in memory, still holding the previous user's data — which is the exact thing we are here
   * to get rid of. Picking the destination here, before the reload, also lands on the RIGHT login
   * screen directly, rather than reloading the current (now-guarded) URL and hoping WithAuthGuard's
   * fallback gets it right — it doesn't: that guard sends every unauthenticated visit to the generic
   * /login regardless of which area it was protecting, so a /client or /vadmin tab reloading in place
   * would silently land on the org login page instead of its own.
   *
   * BUGFIX: this app runs on HashLocationStrategy — every route lives after '#/', nothing before it
   * is a real server path. The previous version set `location.href` straight to e.g. '/client/login',
   * which asks the SERVER for that literal path; since this SPA is only ever served at '/' with
   * everything else client-side, that 404s. And simply prefixing the target with '#/' isn't enough
   * either: assigning `location.href` to a URL that differs from the current one ONLY by its fragment
   * is a same-document navigation in every browser — it updates the address bar but does NOT reload
   * the page, which defeats the whole point of this function. Setting `location.hash` and then
   * explicitly calling `reload()` is what actually forces the full reboot: the hash change first
   * points the (about-to-reload) app at the right route, then reload() re-requests '/' from the
   * server (fragments are never sent over HTTP) and Angular reads the now-updated hash on boot.
   */
  reloadSession() {
    const url = this.router.url || '';
    let target = '/login';
    if (url.startsWith('/client')) target = '/client/login';
    else if (url.startsWith('/vadmin')) target = '/admin/login';

    window.location.hash = target;
    window.location.reload();
  }
}